// ── OCR 헬퍼 — 클라이언트 ───────────────────────────────────────────────────
// 영수증 사진 → /api/ocr-receipt 호출 → 거래 객체로 변환

import { newTransaction, splitVAT, isQualifiedEvidence, genReceiptId } from '../data/taxConstants';

/**
 * File → data URL (base64) 변환
 */
export function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * 이미지 리사이즈 + JPEG 압축 (전송량 ↓, OCR 정확도엔 큰 영향 X)
 * 긴 변 1600px로 제한, JPEG quality 0.85
 */
export async function compressImage(file, maxDim = 1600, quality = 0.85) {
  const dataUrl = await fileToDataUrl(file);
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = dataUrl;
  });
  const ratio = Math.min(1, maxDim / Math.max(img.width, img.height));
  const w = Math.round(img.width * ratio);
  const h = Math.round(img.height * ratio);
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL('image/jpeg', quality);
}

/**
 * OCR API 호출
 */
// 내부 sleep 헬퍼
function sleep(ms) { return new Promise(r => setTimeout(r, ms)); }

export async function ocrReceipt(file, { type = 'expense', onRetry, country = 'KR' } = {}) {
  const dataUrl = await compressImage(file);
  const MAX_RETRY = 2;
  let attempt = 0;
  while (true) {
    const res = await fetch('/api/ocr-receipt', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ image: dataUrl, hint: { type }, country }),
    });
    if (res.ok) {
      const data = await res.json();
      if (!data.ok) {
        if (data.code === 'rate_limit' && attempt < MAX_RETRY) {
          attempt += 1;
          const waitMs = 60000;
          if (onRetry) onRetry({ attempt, waitMs });
          await sleep(waitMs);
          continue;
        }
        throw new Error(data.error || 'OCR 응답 오류');
      }
      data._originalImage = dataUrl;
      return data;
    }
    if ((res.status === 429 || res.status === 503) && attempt < MAX_RETRY) {
      attempt += 1;
      const retryAfter = parseInt(res.headers.get('retry-after') || '60', 10);
      const waitMs = Math.max(retryAfter, 30) * 1000;
      if (onRetry) onRetry({ attempt, waitMs });
      await sleep(waitMs);
      continue;
    }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `OCR 실패 (HTTP ${res.status})`);
  }
}

/**
 * OCR 결과를 거래 객체(Transaction)로 변환
 * @param {object} ocr  /api/ocr-receipt 응답
 * @param {'expense'|'income'} type
 */
export function ocrToTransaction(ocr, type = 'expense') {
  const base = newTransaction(type);

  // 일자
  if (ocr.date && /^\d{4}-\d{2}-\d{2}$/.test(ocr.date)) {
    base.date = ocr.date;
  }

  // 금액 — supply / vat / total
  let supply = Number(ocr.supply) || 0;
  let vat = Number(ocr.vat) || 0;
  let total = Number(ocr.total) || 0;
  if (total === 0 && (supply || vat)) total = supply + vat;
  if (total > 0 && supply === 0 && vat === 0) {
    const split = splitVAT(total);
    supply = split.supply;
    vat = split.vat;
  }
  base.supply = supply;
  base.vat = vat;
  base.total = total;

  // 거래처
  if (ocr.partnerName) base.partnerName = String(ocr.partnerName);

  // 품목 — 여러 개면 콤마로 join
  if (Array.isArray(ocr.items) && ocr.items.length > 0) {
    base.itemName = ocr.items.slice(0, 5).join(', ');
  }

  // 증빙
  const evType = ocr.evidenceType && ['card', 'cash_receipt', 'tax_invoice', 'simple_receipt', 'paper_receipt'].includes(ocr.evidenceType)
    ? ocr.evidenceType
    : 'card';
  base.evidence = {
    type: evType,
    isQualified: isQualifiedEvidence(evType),
    sourceUrl: null,
    externalId: null,
  };
  // ★ top-level evidenceType (ExpenseList/EditModal이 t.evidenceType 직접 읽음)
  base.evidenceType = evType;

  // 카테고리
  if (type === 'expense' && ocr.category?.major) {
    base.category = {
      major: ocr.category.major,
      sub: ocr.category.sub || null,
    };
  }

  // 의제매입 후보 — OCR 결과는 자동 마킹 X, 사용자 확인 필요
  // (OCR이 한국어 영수증을 잘못 읽고 false positive 마킹할 위험 방지)
  if (type === 'expense' && ocr.presumedCandidate) {
    base.vatTreatment = {
      ...base.vatTreatment,
      presumed: false,  // ★ 자동 마킹 X — 사용자 검토 후 토글
      presumedReason: ocr.presumedReason || null,
      presumedConfidence: ocr.confidence || null,
    };
    // 사용자에게 "AI가 의제매입 후보로 판단했음, 확인 필요" 표시용
    base.aiSuggestedPresumed = true;
  }

  // 메타 — OCR 출처는 항상 사용자 확인 권장
  base.source = 'ocr';
  base.confidence = Number(ocr.confidence) || null;
  base.needsReview = true;  // ★ OCR이면 무조건 needsReview
  base.memo = ocr.ocrText ? `[OCR] ${ocr.ocrText.slice(0, 200)}` : '';

  return base;
}

/**
 * lineItems → 거래 객체 배열 (카테고리별로 자동 그룹핑)
 * 사용자가 SplitEditor에서 항목별 카테고리 선택 → 같은 카테고리는 합산해서 1개 거래,
 * 카테고리 다른 것끼리는 거래 N개로 분할.
 *
 * @param {object} ocr  OCR 응답
 * @param {Array} editedLineItems  사용자 편집한 [{itemName, amount, category:{major,sub}, presumedCandidate, presumedReason, included}]
 * @param {'expense'|'income'} type
 * @returns {Array} 거래 객체 배열
 */
export function lineItemsToTransactions(ocr, editedLineItems, type = 'expense') {
  // 포함된 항목만 — 음수(할인 라인) 포함 가능
  const items = (editedLineItems || []).filter(li => li.included !== false && (Number(li.amount) || 0) !== 0);
  if (items.length === 0) {
    return [ocrToTransaction(ocr, type)];  // fallback
  }

  // 카테고리(major+sub) 키로 그룹핑
  const groups = new Map();
  for (const li of items) {
    const major = li.category?.major || 'etc';
    const sub = li.category?.sub || null;
    const key = `${major}::${sub || ''}`;
    if (!groups.has(key)) {
      groups.set(key, {
        category: { major, sub },
        items: [],
        amount: 0,
        anyPresumed: false,
        presumedReasons: [],
      });
    }
    const g = groups.get(key);
    g.items.push(li);
    g.amount += Number(li.amount) || 0;
    if (li.presumedCandidate) {
      g.anyPresumed = true;
      if (li.presumedReason) g.presumedReasons.push(li.presumedReason);
    }
  }

  // 그룹이 1개면 단일 거래 (sourceReceiptId 없이도 OK이지만 일관성 위해 부여)
  // 그룹이 2개 이상이면 분할 거래 (같은 sourceReceiptId 공유)
  const groupArr = Array.from(groups.values());
  const isSplit = groupArr.length > 1;
  const receiptId = isSplit ? genReceiptId() : null;
  const evType = ocr.evidenceType && ['card', 'cash_receipt', 'tax_invoice', 'simple_receipt', 'paper_receipt'].includes(ocr.evidenceType)
    ? ocr.evidenceType : 'card';
  const totalReceiptAmount = Number(ocr.total) || groupArr.reduce((s, g) => s + g.amount, 0);

  return groupArr.map((g, idx) => {
    const base = newTransaction(type);

    if (ocr.date && /^\d{4}-\d{2}-\d{2}$/.test(ocr.date)) {
      base.date = ocr.date;
    }

    // 금액 — 그룹 합산 + 면세/과세 정확 분리 (부가세 신고 정확성)
    const total = g.amount;
    let groupSupply = 0;
    let groupVat = 0;
    let groupTaxFree = 0;
    for (const li of g.items) {
      const amt = Number(li.amount) || 0;
      if (amt === 0) continue;
      if (li.isTaxFree) {
        // 면세: supply에만 포함, vat=0
        groupSupply += amt;
        groupTaxFree += amt;
      } else {
        // 과세: amount는 부가세 포함, supply = amt/1.1, vat = amt - supply
        const supply = Math.round(amt / 1.1);
        groupSupply += supply;
        groupVat += amt - supply;
      }
    }
    // 폴백: 면세/과세 플래그 모두 없으면 기존 splitVAT 사용
    if (groupSupply === 0 && groupVat === 0) {
      const splitVal = splitVAT(total);
      base.supply = splitVal.supply;
      base.vat = splitVal.vat;
    } else {
      base.supply = groupSupply;
      base.vat = groupVat;
    }
    base.total = total;
    if (groupTaxFree > 0) base.taxFreeAmount = groupTaxFree;

    if (ocr.partnerName) base.partnerName = String(ocr.partnerName);

    // 품목명 — 그룹 내 항목들 join (최대 3개 + 외 N개)
    const names = g.items.map(it => it.itemName).filter(Boolean);
    if (names.length === 0) base.itemName = '';
    else if (names.length <= 3) base.itemName = names.join(', ');
    else base.itemName = `${names.slice(0, 3).join(', ')} 외 ${names.length - 3}건`;

    base.evidence = {
      type: evType,
      isQualified: isQualifiedEvidence(evType),
      sourceUrl: null,
      externalId: null,
    };
    base.evidenceType = evType;
    base.evidenceType = evType;

    if (type === 'expense' && g.category.major) {
      base.category = { major: g.category.major, sub: g.category.sub };
    }

    if (type === 'expense' && g.anyPresumed) {
      // V17: 사용자가 LineItemRow에서 토글한 presumedCandidate를 반영.
      // 면세 + presumedCandidate true인 라인만 합산.
      let presumedAmount = 0;
      for (const li of g.items) {
        if (li.presumedCandidate && li.isTaxFree) {
          presumedAmount += Math.max(0, Number(li.amount) || 0);
        }
      }
      base.vatTreatment = {
        ...base.vatTreatment,
        presumed: presumedAmount > 0,  // 사용자 확정 = true
        presumedAmount,
        presumedReason: g.presumedReasons.slice(0, 3).join(' / ') || null,
      };
      base.aiSuggestedPresumed = true;
    }

    if (isSplit) {
      base.sourceReceiptId = receiptId;
      base.splitInfo = {
        totalReceiptAmount,
        splitIndex: idx,
        splitTotal: groupArr.length,
      };
    }

    base.source = 'ocr';
    base.confidence = Number(ocr.confidence) || null;
    base.needsReview = true;

    // 메모 풍부화 — ocrText에 카드사/승인번호/공급가/부가세 등 키워드 포함
    // 사후 편집 모달이 키워드를 감지해서 자동 분류 추천 가능
    const ocrSummary = ocr.ocrText ? ocr.ocrText.slice(0, 300).replace(/\s+/g, ' ').trim() : '';
    const itemPart = base.itemName ? ` · ${base.itemName}` : '';
    base.memo = isSplit
      ? `[OCR 분할 ${idx+1}/${groupArr.length}]${itemPart}${ocrSummary ? ' / ' + ocrSummary : ''}`
      : `[OCR]${itemPart}${ocrSummary ? ' / ' + ocrSummary : ''}`;

    return base;
  });
}

export function ocrToTransactions(ocr, type = 'expense') {
  return [ocrToTransaction(ocr, type)];
}
